/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useMemo, useState, FunctionComponent, ReactNode } from 'react';
import { useFetch } from '@api/ApiHelper';
import {
  Notification,
  NotificationsListResponse,
  NotificationType,
} from '@api/v4/NotificationTypes';
import {
  defaultPagination,
  mapMetaToPagination,
  Pagination
} from '@components/bulk_management/shared';
import { BFLoader } from '@components/common/loader/main';
import { TextButton } from '@components/library/button';
import { ListDropdown, ListOption } from '@components/library/dropdown';
import { NotificationCard } from '@components/profile/NotificationCard';

import classes from './styles/notifications.module.scss';

const MAX_RECORDS = 10000;
const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  [NotificationType.access_request]: bfTranslate('Access Request'),
  [NotificationType.approval]: bfTranslate('Approval'),
  [NotificationType.asset_deletion]: bfTranslate('Asset Deletion'),
  [NotificationType.asset_expiration]: bfTranslate('Asset Expiration'),
  [NotificationType.asset_publish]: bfTranslate('Asset Publish'),
  [NotificationType.asset_removal]: bfTranslate('Asset Removal'),
  [NotificationType.asset_upload]: bfTranslate('Asset Upload'),
  [NotificationType.asset_will_expire]: bfTranslate('Asset Will Expire'),
  [NotificationType.attachment_duplication]: bfTranslate('Attachment Duplication'),
  [NotificationType.comment]: bfTranslate('Comment'),
  [NotificationType.download_request]: bfTranslate('Download Request'),
  [NotificationType.generic]: bfTranslate('Generic'),
  [NotificationType.guest_password_change]: bfTranslate('Guest Password Change'),
  [NotificationType.invite_accepted]: bfTranslate('Invite Accepted'),
  [NotificationType.message]: bfTranslate('Message'),
  [NotificationType.privacy_change]: bfTranslate('Privacy Change'),
  [NotificationType.section_deletion]: bfTranslate('Section Deletion'),
  [NotificationType.share_link_asset_downloaded]: bfTranslate('Share Link Asset Downloaded'),
  [NotificationType.share_link_asset_viewed]: bfTranslate('Share Link Asset Viewed'),
  [NotificationType.share_link_viewed]: bfTranslate('Share Link Viewed'),
  [NotificationType.template_approval]: bfTranslate('Template Approval'),
  [NotificationType.user_groups]: bfTranslate('User Groups')
};

interface NotificationsListProps {
  libraryNameCopy: string;
  notificationTypes: string[];
}

export const NotificationsList: FunctionComponent<NotificationsListProps> = ({
  libraryNameCopy,
  notificationTypes
}) => {
  const [filterType, setFilterType] = useState('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationPagination, setNotificationPagination] = useState(defaultPagination);
  const notificationsBlurb = libraryNameCopy === 'Libraries'
    ? bfTranslate('View updates, alerts, and requests to stay up to speed on your Libraries')
    : bfTranslate('View updates, alerts, and requests to stay up to speed on your Brandfolders');

  const getNotifications = useFetch<NotificationsListResponse>({
    params: {
      'filter[notification_type]': filterType === 'all' ? undefined : filterType,
      page: notificationPagination.currentPage,
      per: notificationPagination.per,
    },
    url: '/api/v4/notifications',
  });

  const filterOptions = useMemo(() => {
    const availableTypes = notificationTypes.map((notificationType) => ({
      label: NOTIFICATION_TYPE_LABELS[NotificationType[notificationType]],
      value: notificationType
    }));
    return [{
      label: bfTranslate('All'),
      value: 'all'
    },
    ...availableTypes];
  }, [notificationTypes]);

  useEffect(() => {
    getNotifications.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, notificationPagination.currentPage, notificationPagination.per]);

  useEffect(() => {
    if (getNotifications.response) {
      setNotifications(
        getNotifications.response.data.map(({ id, attributes }) => ({ ...attributes, id }))
      );
      setNotificationPagination({
        ...notificationPagination,
        ...mapMetaToPagination(getNotifications.response.meta, notificationPagination.per, MAX_RECORDS)
      });
    }
  }, [getNotifications.response]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBodyByFetchState = (): ReactNode => {
    if (getNotifications.error) {
      return (
        <div>
          <p>{bfTranslate('Error retrieving notifications.')}</p>
          <TextButton onClick={(): Promise<void> => getNotifications.fetch()}>
            {bfTranslate('Try again')}
          </TextButton>
        </div>
      );
    }

    if (getNotifications.loading) {
      return <BFLoader />;
    }

    if (getNotifications.response) {
      return (
        <>
          <ul className={classes.notificationList}>
            {notifications.map((notification) => (
              <NotificationCard key={notification.id} {...notification} />
            ))}
          </ul>
          <Pagination
            {...notificationPagination}
            onPageChange={(currentPage): void => {
              setNotificationPagination({ ...notificationPagination, currentPage });
            }}
            onPerChange={(per): void => {
              setNotificationPagination({ ...notificationPagination, per });
            }}
          />
        </>
      );
    }

    return null;
  };

  return (
    <div className={`content profile-container ${classes.notificationsPage}`} id="notifications-page">
      <div className={classes.notificationsHeader}>
        <div>
          <h1>{bfTranslate('Notifications')}</h1>
          <h3 className="sub-title">
            {notificationsBlurb}
          </h3>
        </div>
        <div className={classes.filterNotifications}>
          <ListDropdown
            anchorElementLabel={bfTranslate('Filter by')}
            className={classes.notificationsFilter}
            onChange={({ value }: ListOption): void => {
              setFilterType(value as string);
              setNotificationPagination({ ...notificationPagination, currentPage: 1 });
            }}
            openOnClick
            options={filterOptions}
            value={filterType}
            virtualizeOptions={false}
          />
        </div>
      </div>
      {getBodyByFetchState()}
    </div>
  );
};
